import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../api';
import FilterSearchField from './FilterSearchField';

export default function ExpertiseAreasMaster() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', keywords: '' });
  const [message, setMessage] = useState('');

  const { data: areas = [], isLoading } = useQuery({
    queryKey: ['expertise-areas'],
    queryFn: () => api.get('/expertise-areas').then(r => r.data)
  });

  const saveMutation = useMutation({
    mutationFn: payload =>
      editingId
        ? api.put(`/expertise-areas/${editingId}`, payload).then(r => r.data)
        : api.post('/expertise-areas', payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expertise-areas'] });
      setEditingId(null);
      setForm({ name: '', keywords: '' });
      setMessage(editingId ? 'Area updated.' : 'Area added.');
    },
    onError: err => {
      setMessage(err?.response?.data?.message || 'Save failed.');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/expertise-areas/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expertise-areas'] });
      setMessage('Area removed.');
    },
    onError: err => {
      setMessage(err?.response?.data?.message || 'Delete failed.');
    }
  });

  const filtered = areas.filter(a => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const hay = [a.name, ...(a.keywords || [])].join(' ').toLowerCase();
    return hay.includes(q);
  });

  const startEdit = area => {
    setEditingId(area.id);
    setForm({
      name: area.name,
      keywords: (area.keywords || []).join(', ')
    });
    setMessage('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ name: '', keywords: '' });
  };

  const handleSave = e => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) {
      setMessage('Name is required.');
      return;
    }
    const keywords = form.keywords
      .split(/[,;]+/)
      .map(s => s.trim())
      .filter(Boolean);
    saveMutation.mutate({ name, keywords });
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-[hsl(var(--ds-fg))]">Area of Expertise master</h3>
        <p className="text-sm text-[hsl(var(--ds-muted-fg))] mt-1">
          Domains shown in candidate profiles, filters, and search. Keywords help type-ahead matching
          (e.g. typing &quot;HR&quot; finds Human Resources).
        </p>
      </div>

      <form onSubmit={handleSave} className="border border-[hsl(var(--ds-border))] rounded-xl p-4 bg-[hsl(var(--ds-muted))]/50 space-y-3">
        <p className="text-sm font-medium text-[hsl(var(--ds-fg))]">
          {editingId ? 'Edit area' : 'Add new area'}
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="field-with-label">
            <input
              className="border border-[hsl(var(--ds-border))] px-3 py-2 text-sm w-full rounded-lg bg-[hsl(var(--ds-surface))]"
              placeholder=" "
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            />
            <label>Domain name *</label>
          </div>
          <div className="field-with-label md:col-span-2">
            <input
              className="border border-[hsl(var(--ds-border))] px-3 py-2 text-sm w-full rounded-lg bg-[hsl(var(--ds-surface))]"
              placeholder=" "
              value={form.keywords}
              onChange={e => setForm(f => ({ ...f, keywords: e.target.value }))}
            />
            <label>Search keywords (comma-separated)</label>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] disabled:opacity-60"
          >
            {editingId ? 'Update area' : 'Add area'}
          </button>
          {editingId && (
            <button
              type="button"
              className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))] rounded-lg hover:bg-[hsl(var(--ds-surface))]"
              onClick={cancelEdit}
            >
              Cancel edit
            </button>
          )}
        </div>
      </form>

      {message && <p className="text-sm text-[hsl(var(--ds-fg))]">{message}</p>}

      <FilterSearchField
        placeholder="Search master list by name or keyword…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        onClear={() => setSearch('')}
      />

      <div className="border border-[hsl(var(--ds-border))] rounded-xl overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-[hsl(var(--ds-muted))] text-left text-xs text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide">
            <tr>
              <th className="px-4 py-2 font-semibold">Domain</th>
              <th className="px-4 py-2 font-semibold">Keywords</th>
              <th className="px-4 py-2 font-semibold w-32">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(var(--ds-border))]">
            {isLoading && (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-[hsl(var(--ds-muted-fg))]">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-[hsl(var(--ds-muted-fg))]">
                  No areas found.
                </td>
              </tr>
            )}
            {filtered.map(area => (
              <tr key={area.id} className="hover:bg-[hsl(var(--ds-muted))]">
                <td className="px-4 py-2 font-medium text-[hsl(var(--ds-fg))]">{area.name}</td>
                <td className="px-4 py-2 text-[hsl(var(--ds-muted-fg))]">
                  {(area.keywords || []).join(', ') || '—'}
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <button
                    type="button"
                    className="text-[hsl(var(--ds-primary))] hover:underline text-xs mr-3"
                    onClick={() => startEdit(area)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="text-red-600 hover:underline text-xs"
                    onClick={() => {
                      if (window.confirm(`Remove "${area.name}" from the master list?`)) {
                        deleteMutation.mutate(area.id);
                      }
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
